import DownloadIcon from '@mui/icons-material/Download'
import React, { ReactNode, useCallback, useState } from 'react'
import { Button, useNotify, useRefresh } from 'react-admin'
import { getUrl, httpClient } from '../../providers/data'

interface DownloadResponse {
  id: string
  filename: string
  mimeType: string
  data: string
}

interface CertificateDownloadButtonProps {
  certificateId?: string
  label?: string
  renderAs?: (onClick: () => void, loading: boolean) => ReactNode
}

const base64ToBlob = (b64: string, mime: string): Blob => {
  const byteChars = atob(b64)
  const bytes = new Uint8Array(byteChars.length)
  // eslint-disable-next-line security/detect-object-injection
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

const CertificateDownloadButton: React.FC<CertificateDownloadButtonProps> = ({
  certificateId,
  label = 'resources.certificates.actions.downloadCertificate',
  renderAs,
}) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = useState(false)

  const onClick = useCallback(() => {
    setLoading(true)
    httpClient(`${getUrl()}/certificate/${certificateId}/download`, { method: 'GET' })
      .then(({ json }: { json: DownloadResponse }) => {
        const blob = base64ToBlob(json.data, json.mimeType || 'application/x-pem-file')
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = json.filename || `certificate-${json.id}.crt.pem`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        notify('resources.certificates.actions.certificateDownloaded', { type: 'info' })
        refresh()
      })
      .catch((e: unknown) => {
        console.error(e)
        notify('ra.notification.http_error', { type: 'warning' })
      })
      .finally(() => setLoading(false))
  }, [certificateId, notify, refresh])

  if (renderAs) return <>{renderAs(onClick, loading)}</>

  return (
    <Button
      label={label}
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={onClick}
      disabled={loading}
    />
  )
}

export default CertificateDownloadButton

